define([
    'lodash'
], function (_) {
    'use strict';

    const pageRelatedLinkTypes = ['PageLink', 'AnchorLink'];
    const sliceHashTag = dataItem => dataItem.replace(/^#/, '');

    const extractMenus = masterPage => {
        const masterPageDocumentData = masterPage.data.document_data;
        return _(masterPageDocumentData).get('CUSTOM_MENUS.menus', [])
            .reduce((menusAcc, menu) => {
                const menuItemId = sliceHashTag(menu);
                const menuItem = _.get(masterPageDocumentData, menuItemId);
                return _.set(menusAcc, menuItemId, menuItem);
            }, {});
    };

    const getMenuDataPointers = (masterPage, menu) => {
        const resolveDataPointer = ({dataId, parent = null}) => {
            const dataPath = `data.document_data.${sliceHashTag(dataId)}`;
            const childrenPaths = [];
            const children = _.get(masterPage, `${dataPath}.items`);
            if (children.length > 0) {
                childrenPaths.push(children.map(itemId =>
                    resolveDataPointer({dataId: itemId, parent: sliceHashTag(dataId)})));
            }
            return childrenPaths.concat([{path: dataPath, parent}]);
        };

        return _.flattenDeep(resolveDataPointer({dataId: menu}));
    };

    return {
        serialize: ({pagesData}) => {
            const payload = {menus: {}, pages: {}};
            const menus = extractMenus(pagesData.masterPage);
            const dataItemsPathsByMenu = _.reduce(menus, (menusAcc, _menuItem, menuType) =>
                _.set(menusAcc, menuType, getMenuDataPointers(pagesData.masterPage, menuType)), {});
            const shouldAddMenuDataToPage = linkItem => linkItem && pageRelatedLinkTypes.includes(linkItem.type) && sliceHashTag(linkItem.pageId) !== 'masterPage';

            _.forEach(dataItemsPathsByMenu, (dataItemsPath, menuType) => {
                payload.menus[menuType] = payload.menus[menuType] || {data: {}, structure: {}};
                _.forEach(dataItemsPath, ({path}) => {
                    const menuItem = _.get(pagesData.masterPage, path);
                    const linkItem = menuItem.link && _.get(pagesData.masterPage, `data.document_data.${sliceHashTag(menuItem.link)}`);

                    if (linkItem && linkItem.target === '_self') {
                        _.omit(linkItem, ['target']);
                    }

                    const dataItems = _.assign({}, {[menuItem.id]: _.omit(menuItem, 'items')},
                        linkItem ? {[linkItem.id]: linkItem} : {});
                    _.set(payload.menus[menuType], `structure.${menuItem.id}`, {id: menuItem.id, items: menuItem.items});

                    // Merge the data items to either the menu object (if not related to a specific page)
                    // For instance external links, phone etc
                    if (shouldAddMenuDataToPage(linkItem)) {
                        const pageId = sliceHashTag(linkItem.pageId);
                        _.set(payload.pages, `${pageId}.content.menus.${menuType}`, dataItems);
                    } else {
                        _.merge(payload.menus[menuType].data, dataItems);
                    }
                    _.forEach(dataItems, (_dataItem, dataItem) =>
                        _.unset(pagesData.masterPage.data.document_data, dataItem)
                    );
                    _.unset(pagesData.masterPage, path);
                });
            });

            return payload;
        },
        deserialize: payload => {
            const dataItems = _.reduce(payload.menus, (menusAcc, {structure, data: nonPageDataItems}, menuType) => {
                const menuItemsExistingInMenu = _(structure).map('items').flattenDeep().map(sliceHashTag).value();
                const pageMenuDataItems = _.reduce(payload.pages, (pageAccum, page) =>
                    _.assign(pageAccum, _.get(page.content, `menus.${menuType}`), {}), {});
                const missingItems = _(pageMenuDataItems).filter(pmdi =>
                    pmdi.type === 'BasicMenuItem' && !menuItemsExistingInMenu.includes(pmdi.id))
                    .map(dataItem => `#${dataItem.id}`).value();
                structure[menuType].items.push(...missingItems);
                return _.merge(menusAcc, pageMenuDataItems, nonPageDataItems, structure, missingItems);
            }, {});

            return {
                pagesData: {
                    masterPage: {
                        data: {
                            document_data: dataItems
                        }
                    }
                }
            };
        },
        getEncryptedPaths: payload => _(payload.menus).keys().map(menu => `menus.${menu}`).concat(
            _(payload.pages).keys().map(pageId => `pages.${pageId}.content.menus`).value())
            .value()
    };
});
